// Documents — NDA / MSA / SOW / contract templates and the sent/drafted instances built from them.
// Templates carry {{merge_field}} placeholders; a document is a rendered, tracked instance.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select } from "@/components/ui/select";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { AVAILABLE_TOKENS } from "@/lib/merge-fields";
import { useAuthStore } from "@/stores/auth-store";
import { Plus, X, Trash2, Send, FileText } from "lucide-react";

const DOC_TYPES = ["NDA", "MSA", "SOW", "CONSULTING_AGREEMENT", "STAFFING_AGREEMENT", "OFFER_LETTER", "OTHER"] as const;
type DocType = (typeof DOC_TYPES)[number];
const TYPE_LABEL: Record<DocType, string> = {
  NDA: "NDA",
  MSA: "Master Service Agreement",
  SOW: "Statement of Work",
  CONSULTING_AGREEMENT: "Consulting Agreement",
  STAFFING_AGREEMENT: "Staffing Agreement",
  OFFER_LETTER: "Offer Letter",
  OTHER: "Other",
};

const DOC_STATUSES = ["DRAFT", "SENT", "VIEWED", "SIGNED", "VOID"] as const;
type DocStatus = (typeof DOC_STATUSES)[number];
const STATUS_TONE: Record<DocStatus, "secondary" | "outline" | "default"> = {
  DRAFT: "outline", SENT: "secondary", VIEWED: "secondary", SIGNED: "default", VOID: "outline",
};

interface Template {
  id: string; name: string; type: DocType; subject: string; bodyHtml?: string;
  isShared: boolean; userId?: string | null; user?: { id: string; name: string };
}
interface DocRow {
  id: string; title: string; type: DocType; status: DocStatus;
  recipientName?: string | null; recipientEmail?: string | null;
  createdAt: string; sentAt?: string | null;
  template?: { name: string; type: string } | null;
  user?: { id: string; name: string } | null;
  contact?: { id: string; name: string } | null;
  account?: { id: string; name: string } | null;
}

const EMPTY_TPL = { name: "", type: "NDA" as DocType, subject: "", bodyHtml: "", isShared: false };
const EMPTY_DOC = { templateId: "", title: "", type: "NDA" as DocType, recipientName: "", recipientEmail: "", bodyHtml: "" };

export default function DocumentsPage() {
  const me = useAuthStore((s) => s.me);
  const qc = useQueryClient();
  const isAdmin = me?.role === "SUPER_ADMIN" || me?.role === "ADMIN";
  const [tab, setTab] = useState<"documents" | "templates">("documents");

  return (
    <>
      <PageHeader
        title="Documents"
        description="NDAs, MSAs, SOWs and other contracts — build from a template, send via your Outlook, and track signing status."
      />
      <div className="p-6 space-y-4">
        <div className="flex gap-1 border-b">
          {(["documents", "templates"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`-mb-px border-b-2 px-3 py-1.5 text-sm capitalize ${tab === t ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              {t}
            </button>
          ))}
        </div>
        {tab === "documents"
          ? <DocumentsTab qc={qc} />
          : <TemplatesTab qc={qc} isAdmin={isAdmin} meId={me?.id} />}
      </div>
    </>
  );
}

// ─── Documents tab ────────────────────────────────────────────────────────────

function DocumentsTab({ qc }: { qc: ReturnType<typeof useQueryClient> }) {
  const [draft, setDraft] = useState({ ...EMPTY_DOC });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data: docs, isLoading } = useQuery({
    queryKey: ["/documents"],
    queryFn: async () => (await api.get("/documents")).data.data as DocRow[],
  });
  const { data: templates } = useQuery({
    queryKey: ["/documents/templates"],
    queryFn: async () => (await api.get("/documents/templates")).data.data as Template[],
  });

  async function pickTemplate(id: string) {
    if (!id) { setDraft((d) => ({ ...d, templateId: "" })); return; }
    const full = (await api.get(`/documents/templates/${id}`)).data as Template;
    setDraft((d) => ({
      ...d,
      templateId: id,
      type: full.type,
      title: d.title || full.name,
      bodyHtml: full.bodyHtml ?? "",
    }));
  }

  const saveMut = useMutation({
    mutationFn: async (sendNow: boolean) => {
      const payload = {
        templateId: draft.templateId || undefined,
        title: draft.title,
        type: draft.type,
        bodyHtml: draft.bodyHtml,
        recipientName: draft.recipientName || undefined,
        recipientEmail: draft.recipientEmail || undefined,
        sendNow,
      };
      return (await api.post("/documents", payload)).data;
    },
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ["/documents"] });
      setDraft({ ...EMPTY_DOC });
      setError(null);
      setNotice(res?.emailWarning ? `Saved, but email failed: ${res.emailWarning}` : null);
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? "Save failed."),
  });

  const statusMut = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: DocStatus }) =>
      (await api.patch(`/documents/${id}`, { status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/documents"] }),
  });
  const delMut = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/documents/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/documents"] }),
  });

  const canSend = !!draft.recipientEmail;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
      <Card>
        <CardContent className="p-0">
          <Table>
            <Thead>
              <Tr><Th>Document</Th><Th>Type</Th><Th>Recipient</Th><Th>Status</Th><Th></Th></Tr>
            </Thead>
            <Tbody>
              {isLoading && <Tr><Td colSpan={5} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
              {!isLoading && (docs?.length ?? 0) === 0 && (
                <Tr><Td colSpan={5} className="py-8 text-center text-muted-foreground">No documents yet. Create one from a template →</Td></Tr>
              )}
              {docs?.map((d) => (
                <Tr key={d.id} className="group">
                  <Td>
                    <div className="flex items-center gap-2 font-medium"><FileText className="h-4 w-4 text-muted-foreground" />{d.title}</div>
                    <div className="text-xs text-muted-foreground">{d.account?.name ?? d.contact?.name ?? "—"} · {new Date(d.createdAt).toLocaleDateString()}</div>
                  </Td>
                  <Td className="text-xs text-muted-foreground">{TYPE_LABEL[d.type] ?? d.type}</Td>
                  <Td className="text-xs">{d.recipientName || d.recipientEmail || "—"}</Td>
                  <Td>
                    <Select
                      value={d.status}
                      onChange={(e) => statusMut.mutate({ id: d.id, status: e.target.value as DocStatus })}
                      className="h-7 text-xs"
                    >
                      {DOC_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </Select>
                  </Td>
                  <Td>
                    <button
                      onClick={() => { if (confirm(`Delete "${d.title}"?`)) delMut.mutate(d.id); }}
                      className="opacity-0 group-hover:opacity-100 text-destructive"
                      aria-label="Delete"
                    ><Trash2 className="h-4 w-4" /></button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-3">
          <h3 className="text-sm font-semibold">New document</h3>
          <div>
            <Label>From template</Label>
            <Select value={draft.templateId} onChange={(e) => pickTemplate(e.target.value)}>
              <option value="">— Blank —</option>
              {templates?.map((t) => <option key={t.id} value={t.id}>{t.name} ({TYPE_LABEL[t.type] ?? t.type})</option>)}
            </Select>
          </div>
          <div>
            <Label htmlFor="d-title">Title</Label>
            <Input id="d-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="e.g. Acme Corp — Mutual NDA" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="d-type">Type</Label>
              <Select id="d-type" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value as DocType })}>
                {DOC_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
              </Select>
            </div>
            <div>
              <Label htmlFor="d-rname">Recipient name</Label>
              <Input id="d-rname" value={draft.recipientName} onChange={(e) => setDraft({ ...draft, recipientName: e.target.value })} />
            </div>
          </div>
          <div>
            <Label htmlFor="d-remail">Recipient email</Label>
            <Input id="d-remail" type="email" value={draft.recipientEmail} onChange={(e) => setDraft({ ...draft, recipientEmail: e.target.value })} placeholder="needed to send" />
          </div>
          <div>
            <Label htmlFor="d-body">Body</Label>
            <textarea
              id="d-body"
              value={draft.bodyHtml}
              onChange={(e) => setDraft({ ...draft, bodyHtml: e.target.value })}
              className="min-h-[220px] w-full rounded-md border bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder="Document body (HTML allowed). Use a template to prefill."
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
          {notice && <p className="text-sm text-amber-600">{notice}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={!draft.title || !draft.bodyHtml || saveMut.isPending} onClick={() => saveMut.mutate(false)}>
              Save draft
            </Button>
            <Button disabled={!draft.title || !draft.bodyHtml || !canSend || saveMut.isPending} onClick={() => saveMut.mutate(true)} title={canSend ? "Send via your Outlook" : "Add a recipient email to send"}>
              <Send className="mr-1 h-4 w-4" />{saveMut.isPending ? "Working…" : "Save & send"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Templates tab ──────────────────────────────────────────────────────────

function TemplatesTab({ qc, isAdmin, meId }: { qc: ReturnType<typeof useQueryClient>; isAdmin: boolean; meId?: string }) {
  const [editing, setEditing] = useState<Template | null>(null);
  const [draft, setDraft] = useState({ ...EMPTY_TPL });
  const [showMerge, setShowMerge] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["/documents/templates"],
    queryFn: async () => (await api.get("/documents/templates")).data.data as Template[],
  });

  function openNew() { setEditing(null); setDraft({ ...EMPTY_TPL }); setError(null); }
  async function openEdit(t: Template) {
    const full = (await api.get(`/documents/templates/${t.id}`)).data as Template;
    setEditing(full);
    setDraft({ name: full.name, type: full.type, subject: full.subject, bodyHtml: full.bodyHtml ?? "", isShared: full.isShared });
    setError(null);
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = { name: draft.name, type: draft.type, subject: draft.subject, bodyHtml: draft.bodyHtml, isShared: draft.isShared };
      if (editing) return (await api.put(`/documents/templates/${editing.id}`, payload)).data;
      return (await api.post("/documents/templates", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/documents/templates"] }); openNew(); },
    onError: (err: any) => setError(err?.response?.data?.message ?? "Save failed."),
  });
  const delMut = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/documents/templates/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/documents/templates"] }),
  });

  function insertToken(token: string) {
    setDraft((d) => ({ ...d, bodyHtml: d.bodyHtml + token }));
    setShowMerge(false);
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardContent className="p-0">
          <Table>
            <Thead><Tr><Th>Name</Th><Th>Type</Th><Th>Scope</Th><Th></Th></Tr></Thead>
            <Tbody>
              {isLoading && <Tr><Td colSpan={4} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
              {!isLoading && (data?.length ?? 0) === 0 && (
                <Tr><Td colSpan={4} className="py-8 text-center text-muted-foreground">No templates yet.</Td></Tr>
              )}
              {data?.map((t) => (
                <Tr key={t.id} className="group">
                  <Td><button onClick={() => openEdit(t)} className="font-medium hover:underline">{t.name}</button></Td>
                  <Td className="text-xs text-muted-foreground">{TYPE_LABEL[t.type] ?? t.type}</Td>
                  <Td>{t.isShared ? <Badge variant="secondary">Shared</Badge> : <Badge variant="outline">Mine</Badge>}</Td>
                  <Td>
                    {((!t.userId && isAdmin) || t.userId === meId) && (
                      <button onClick={() => { if (confirm(`Delete "${t.name}"?`)) delMut.mutate(t.id); }} className="opacity-0 group-hover:opacity-100 text-destructive" aria-label="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">{editing ? "Edit template" : "New template"}</h3>
            {editing && <button onClick={openNew} aria-label="Close" className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="t-name">Name</Label>
              <Input id="t-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="t-type">Type</Label>
              <Select id="t-type" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value as DocType })}>
                {DOC_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="t-subj">Subject</Label>
            <Input id="t-subj" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} placeholder="Email subject when sending" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="t-body">Body</Label>
              <div className="relative">
                <button type="button" onClick={() => setShowMerge((v) => !v)} className="rounded-md border px-2 py-1 text-xs hover:bg-muted">Insert merge field</button>
                {showMerge && (
                  <div className="absolute right-0 top-full z-10 mt-1 w-64 rounded-md border bg-popover shadow-lg">
                    <ul className="max-h-64 overflow-y-auto text-sm">
                      {AVAILABLE_TOKENS.map((t) => (
                        <li key={t.token}>
                          <button type="button" onClick={() => insertToken(t.token)} className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-muted">
                            <span>{t.label}</span><code className="text-xs text-muted-foreground">{t.token}</code>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <textarea
              id="t-body"
              value={draft.bodyHtml}
              onChange={(e) => setDraft({ ...draft, bodyHtml: e.target.value })}
              className="min-h-[220px] w-full rounded-md border bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder="This Agreement is entered into between {{account.name}} and CBC…"
            />
          </div>
          <div className="flex items-center gap-2">
            <input id="t-shared" type="checkbox" checked={draft.isShared} onChange={(e) => setDraft({ ...draft, isShared: e.target.checked })} disabled={!isAdmin} />
            <Label htmlFor="t-shared">Share with whole org{!isAdmin && <span className="ml-1 text-xs text-muted-foreground">(admin only)</span>}</Label>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            {editing && <Button variant="outline" onClick={openNew}>Cancel</Button>}
            <Button onClick={() => saveMut.mutate()} disabled={!draft.name || !draft.subject || !draft.bodyHtml || saveMut.isPending}>
              {saveMut.isPending ? "Saving…" : editing ? "Update" : <><Plus className="mr-1 h-4 w-4" />Create</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
